<script setup lang="ts">
import { useMediaQuery, useResizeObserver } from '@vueuse/core';
import { computed, onMounted, onUpdated, ref } from 'vue';

import type { FilterBarItem } from './filter';

import { BaseDropdown } from '../dropdown';
import { IconChevronDown } from '../icons';
import { BaseSidePanel } from '../side-panel';
import { countVisibleFilters } from './filter';

/**
 * Шапка журнала в один ряд: слева `leading` (вкладки, счётчик), справа фильтры и `search`.
 * Фильтр лежит в слоте с именем своего `key`. Что не поместилось, уходит в «Ещё» с конца списка,
 * на телефоне все фильтры открываются шторкой.
 */
const props = defineProps<{ filters: readonly FilterBarItem[] }>();

const ROW_GAP = 16;
const FILTER_GAP = 8;
const MORE_WIDTH = 104;

const rootRef = ref<HTMLElement | null>(null);
const leadingRef = ref<HTMLElement | null>(null);
const searchRef = ref<HTMLElement | null>(null);
const itemRefs = new Map<string, HTMLElement>();

const rootWidth = ref(0);
const leadingWidth = ref(0);
const searchWidth = ref(0);
const itemWidths = ref<Record<string, number>>({});

const isCompact = useMediaQuery('(max-width: 639px)');
const isSheetOpen = ref(false);

const setItemRef = (key: string, element: unknown): void => {
  if (element instanceof HTMLElement) itemRefs.set(key, element);
  else itemRefs.delete(key);
};

// Скрытый фильтр не измерить, поэтому держим ширину, снятую, пока он был в ряду.
const measure = (): void => {
  rootWidth.value = rootRef.value?.clientWidth ?? 0;
  leadingWidth.value = leadingRef.value?.offsetWidth ?? 0;
  searchWidth.value = searchRef.value?.offsetWidth ?? 0;

  itemRefs.forEach((element, key) => {
    if (itemWidths.value[key] !== element.offsetWidth) {
      itemWidths.value = { ...itemWidths.value, [key]: element.offsetWidth };
    }
  });
};

onMounted(measure);
onUpdated(measure);
useResizeObserver(rootRef, measure);

const filtersSpace = computed(() => rootWidth.value - (searchWidth.value ? searchWidth.value + FILTER_GAP : 0));

// Длинные вкладки не сжимаем: если рядом не остаётся места, фильтры уходят на свою строку.
const isStacked = computed(
  () => !isCompact.value && leadingWidth.value > 0 && leadingWidth.value + ROW_GAP + MORE_WIDTH > filtersSpace.value,
);

const visibleCount = computed(() => {
  if (isCompact.value) return 0;
  if (rootWidth.value === 0) return props.filters.length;

  const available = filtersSpace.value - (isStacked.value || !leadingWidth.value ? 0 : leadingWidth.value + ROW_GAP);

  return countVisibleFilters(
    props.filters.map(({ key }) => itemWidths.value[key] ?? MORE_WIDTH),
    available,
    MORE_WIDTH,
    FILTER_GAP,
  );
});

const visibleFilters = computed(() => props.filters.slice(0, visibleCount.value));
const hiddenFilters = computed(() => props.filters.slice(visibleCount.value));
const hiddenActiveCount = computed(() => hiddenFilters.value.filter(({ active }) => active).length);

const TRIGGER_CLASS =
  'flex h-11 cursor-pointer items-center justify-center gap-2 rounded-lg border border-(--border) px-4 text-sm whitespace-nowrap text-(--text-primary) transition-[background-color,box-shadow,transform] duration-150 hover:bg-(--surface-hover) focus-visible:ring-2 focus-visible:ring-(--accent) focus-visible:outline-none active:scale-[0.96]';
const COUNT_CLASS =
  'shrink-0 rounded-full bg-(--accent)/15 px-1.5 py-px text-xs leading-tight text-(--accent) tabular-nums';
// Триггеры фильтров рассчитаны на ряд, в колонке они растягиваются на всю ширину.
const STACK_ITEM_CLASS = '[&_button]:w-full [&_button>span:first-of-type]:flex-1 [&>div>div]:w-full';
</script>

<template>
  <div
    ref="rootRef"
    class="flex min-w-0 gap-3"
    :class="isCompact || isStacked ? 'flex-col' : 'items-center gap-4'"
  >
    <div
      v-if="$slots.leading"
      ref="leadingRef"
      class="flex w-fit max-w-full shrink-0 items-center gap-3"
    >
      <slot name="leading" />
    </div>

    <div
      class="flex min-w-0 flex-1 items-center gap-2"
      :class="isCompact ? '' : 'justify-end'"
    >
      <div
        v-for="filter in visibleFilters"
        :key="filter.key"
        :ref="(element) => setItemRef(filter.key, element)"
        class="shrink-0"
      >
        <slot :name="filter.key" />
      </div>

      <BaseDropdown
        v-if="!isCompact && hiddenFilters.length > 0"
        width="min(280px, calc(100vw - 2rem))"
        class="shrink-0"
        :content="{ align: 'end', side: 'bottom', sideOffset: 8 }"
      >
        <template #trigger="{ isOpen, toggle }">
          <button
            type="button"
            :class="[TRIGGER_CLASS, 'w-full']"
            :aria-expanded="isOpen"
            @click="toggle"
          >
            Ещё
            <span
              v-if="hiddenActiveCount > 0"
              :class="COUNT_CLASS"
              >{{ hiddenActiveCount }}</span
            >
            <IconChevronDown
              aria-hidden="true"
              class="shrink-0 transition-transform duration-150"
              :class="{ 'rotate-180': isOpen }"
              :width="10"
              :height="10"
            />
          </button>
        </template>
        <template #content>
          <div class="flex flex-col gap-2 p-2">
            <div
              v-for="filter in hiddenFilters"
              :key="filter.key"
              :class="STACK_ITEM_CLASS"
            >
              <slot :name="filter.key" />
            </div>
          </div>
        </template>
      </BaseDropdown>

      <div
        v-if="$slots.search"
        ref="searchRef"
        :class="isCompact ? 'min-w-0 flex-1' : 'shrink-0'"
      >
        <slot name="search" />
      </div>

      <button
        v-if="isCompact && filters.length > 0"
        type="button"
        :class="[TRIGGER_CLASS, $slots.search ? 'shrink-0' : 'w-full']"
        @click="isSheetOpen = true"
      >
        Фильтры
        <span
          v-if="hiddenActiveCount > 0"
          :class="COUNT_CLASS"
          >{{ hiddenActiveCount }}</span
        >
      </button>
    </div>

    <BaseSidePanel
      v-if="isCompact"
      :open="isSheetOpen"
      side="bottom"
      label="Фильтры"
      @close="isSheetOpen = false"
    >
      <template #header><h2 class="text-sm font-semibold text-(--text-primary)">Фильтры</h2></template>
      <div class="flex flex-col gap-2 p-4">
        <div
          v-for="filter in hiddenFilters"
          :key="filter.key"
          :class="STACK_ITEM_CLASS"
        >
          <slot :name="filter.key" />
        </div>
      </div>
    </BaseSidePanel>
  </div>
</template>
